/**
 * Pure decisions behind the empty chat's personal greeting (spec: personal
 * welcome, 2026-09). Kept out of ChatThread.jsx so they run without a DOM —
 * this repo's vitest config has no jsdom environment (vitest.config.js).
 */

/** Ranks that see the admin-flavoured welcome. Mirrors utils/adminAccess.js. */
const ADMIN_RANKS = ['admin', 'tenant_admin'];

/**
 * A first name guessed from the local part of an e-mail address or a
 * username — the fallback for accounts with no platform name claim
 * (src/server/oidc/claims.js) and for local/password accounts, which have
 * none at all.
 *
 * Only the first `.`/`_`/`-`-separated segment is read, and only when it is
 * nothing but letters and at least two of them. `m-t1` therefore yields no
 * name (`m` is one letter, and `t1` is plainly not a name) while
 * `katrin.vogelsang` yields `Katrin`: a real first name is common enough in
 * that shape, and a wrong guess here is a person greeted by their own e-mail
 * prefix, not a leaked fact.
 *
 * @param {unknown} value - an e-mail address or a bare username.
 * @returns {string|null}
 */
export function firstNameFromLocalPart(value) {
  if (typeof value !== 'string') return null;
  const local = value.split('@')[0].trim();
  if (!local) return null;

  const [candidate] = local.split(/[._-]+/).filter(Boolean);
  if (!candidate || !/^[A-Za-zÀ-ÖØ-öø-ÿ]{2,}$/.test(candidate)) return null;

  return candidate.charAt(0).toUpperCase() + candidate.slice(1).toLowerCase();
}

/**
 * The first name to greet with, in priority order: the platform-claim name
 * GET /api/auth/me stored (`users.first_name`), else a guess from the e-mail,
 * else a guess from the username, else none — the caller then shows a
 * name-less greeting rather than inventing one.
 *
 * @param {{firstName?: string|null, email?: string|null, username?: string|null}} [user]
 * @returns {string|null}
 */
export function deriveGreetingFirstName({ firstName, email, username } = {}) {
  const stored = typeof firstName === 'string' ? firstName.trim() : '';
  if (stored) return stored;
  return firstNameFromLocalPart(email) || firstNameFromLocalPart(username) || null;
}

/**
 * The greeting's translation key + interpolation params, for
 * `t(key, params)`. Kept as data rather than a rendered string so DE and EN
 * both come from the same decision.
 *
 * @param {string|null} [firstName]
 * @returns {{key: string, params: object}}
 */
export function resolveGreeting(firstName) {
  const name = typeof firstName === 'string' ? firstName.trim() : '';
  return name
    ? { key: 'chat.greetingWithName', params: { name } }
    : { key: 'chat.greeting', params: {} };
}

/**
 * The knowledge welcome's title: "Wissen von {tenant}" once a tenant name is
 * known (GET /api/auth/me's `tenant_name`, resolved from the platform
 * tenant's displayName/name — src/server/oidc/internal-api.js
 * getCachedTenant()), else the neutral standalone title. The same fallback
 * covers both "no tenant" and "tenant name unknown right now" (the platform
 * lookup failed) — a person never sees a broken title, only a generic one.
 *
 * @param {string|null} [tenantName]
 * @returns {{key: string, params: object}}
 */
export function resolveKnowledgeTitle(tenantName) {
  const name = typeof tenantName === 'string' ? tenantName.trim() : '';
  return name
    ? { key: 'chat.knowledgeTitle', params: { tenant: name } }
    : { key: 'chat.knowledgeTitleStandalone', params: {} };
}

/**
 * Which welcome copy a role sees: admins get questions about content and
 * onboarding, everyone else (learners, and any role this instance doesn't
 * know yet) gets questions about their own learning material.
 *
 * @param {string|null|undefined} role
 * @returns {'admin'|'learner'}
 */
export function resolveKnowledgeAudience(role) {
  return ADMIN_RANKS.includes(role) ? 'admin' : 'learner';
}
